import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDashboard, useRecentJobsGlobal } from '../hooks/useSDIExtended'
import { PageHeader } from '@/shared/components/ui/PageHeader'
import { KpiCard } from '@/shared/components/ui/KpiCard'
import { EmptyState } from '@/shared/components/ui/EmptyState'
import { Button } from '@/shared/components/ui/Button'
import { FilterBar, type FilterState } from '../components/sdi/shared/FilterBar'
import type { DashboardData } from '../hooks/useSDIExtended'
import type { JobRunItem } from '../hooks/sdi.types'
import {
  CheckCircle2, AlertCircle, XCircle, ChevronRight,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { CHART_COLORS, CHART_DEFAULTS } from '@/shared/lib/chartTheme'
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceArea, Cell,
} from 'recharts'

/* eslint-disable @typescript-eslint/no-explicit-any */

const fmtCompact = new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 })
const fmtPct = new Intl.NumberFormat('fr-FR', { style: 'percent', minimumFractionDigits: 1 })
const fmtEur = new Intl.NumberFormat('fr-FR', { notation: 'compact', style: 'currency', currency: 'EUR', maximumFractionDigits: 1 })

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—'
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `il y a ${mins}min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `il y a ${hours}h`
  return `il y a ${Math.floor(hours / 24)}j`
}

const jobIcon = (status: string) => {
  if (status === 'ok') return <CheckCircle2 size={14} className="text-semantic-success" />
  if (status === 'error') return <XCircle size={14} className="text-semantic-danger" />
  return <AlertCircle size={14} className="text-semantic-warning" />
}

function MarketTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: DashboardData['markets'][0] }> }) {
  if (!active || !payload?.[0]) return null
  const m = payload[0].payload
  return (
    <div style={CHART_DEFAULTS.tooltip.contentStyle}>
      <p className="font-semibold text-paper mb-1">{m.name}</p>
      <p style={{ color: CHART_COLORS.paper }}>Demande: {fmtCompact.format(m.market_size)}</p>
      <p style={{ color: CHART_COLORS.paper }}>Capture: {fmtPct.format(m.capture_rate)}</p>
      <p style={{ color: CHART_COLORS.tertiary }}>Perdu: {fmtEur.format(m.lost_value)}</p>
    </div>
  )
}

export function SDIDashboardPage() {
  const navigate = useNavigate()
  const [filterParams, setFilterParams] = useState<Partial<FilterState>>({})
  const { data: dash, isLoading } = useDashboard(filterParams as Record<string, string>)
  const { data: recentJobs } = useRecentJobsGlobal(10)

  const markets = dash?.markets ?? []
  const countries = useMemo(() => [...new Set(markets.map((m) => m.country))].sort(), [markets])
  const scatterData = markets.filter((m) => m.market_size > 0)
  const medianSize = scatterData.length ? [...scatterData].sort((a, b) => a.market_size - b.market_size)[Math.floor(scatterData.length / 2)].market_size : 0
  const topOpps = [...markets].sort((a, b) => b.lost_value - a.lost_value).slice(0, 5)

  return (
    <div className="space-y-6">
      <PageHeader
        moduleId="sdi"
        moduleFullName="Search Demand Intelligence"
        title="Vue d'ensemble"
        subtitle="Analyse de la demande et opportunités de marché"
        actions={
          <Button variant="gold" onClick={() => navigate('/sdi/markets')}>
            Voir tous les marchés <ChevronRight size={14} />
          </Button>
        }
      />

      <FilterBar countries={countries} onFilterChange={(f: FilterState) => setFilterParams({ country: f.country || undefined })} />

      {/* KPI Strip — 3px filet, no icons, teal accent */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard label="Marchés actifs" value={dash?.active_markets ?? 0} accentColor="#0F7173" loading={isLoading} variant="md" />
        <KpiCard label="Demande totale" value={fmtCompact.format(dash?.total_market_size ?? 0)} unit="req/mois" accentColor="#0F7173" loading={isLoading} variant="md" />
        <KpiCard label="Capture globale" value={dash?.avg_capture_rate ? fmtPct.format(dash.avg_capture_rate) : '—'} accentColor="#1E6B3A" loading={isLoading} variant="md" />
        <KpiCard label="Opportunité perdue" value={fmtEur.format(dash?.total_lost_value ?? 0)} accentColor="#922B21" loading={isLoading} variant="md" />
        <KpiCard label="Budget total" value={fmtEur.format(dash?.total_budget ?? 0)} unit="/mois" accentColor="#0F7173" loading={isLoading} variant="md" />
        <KpiCard label="Confiance moyenne" value={dash?.avg_confidence ? `${dash.avg_confidence.toFixed(0)}/100` : '—'} accentColor="#C9A84C" loading={isLoading} variant="md" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scatter Chart */}
        <div className="lg:col-span-2 bg-white rounded-card border border-ink-border p-5 shadow-card">
          <h3 className="font-serif text-sm font-semibold text-ink mb-4">Performance des marchés</h3>
          {isLoading ? (
            <div className="h-[320px] bg-paper-warm rounded animate-pulse" />
          ) : scatterData.length === 0 ? (
            <EmptyState compact title="Aucun marché avec données"
              description="Créez un marché et lancez une synchronisation pour voir les données."
              action={{ label: '+ Nouveau marché', onClick: () => navigate('/sdi/markets'), variant: 'primary' }} />
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <ScatterChart margin={{ top: 10, right: 10, bottom: 30, left: 10 }}>
                <CartesianGrid {...CHART_DEFAULTS.grid} />
                <XAxis type="number" dataKey="capture_rate" name="Capture" tickFormatter={(v: number) => fmtPct.format(v)} {...CHART_DEFAULTS.axis} />
                <YAxis type="number" dataKey="market_size" name="Taille" tickFormatter={(v: number) => fmtCompact.format(v)} {...CHART_DEFAULTS.axis} />
                <ZAxis type="number" dataKey="lost_value" range={[80, 500]} />
                <ReferenceArea x1={0} x2={0.30} y1={medianSize} y2={scatterData.reduce((max, m) => Math.max(max, m.market_size), 0) * 1.1}
                  fill="#FFF3EE" fillOpacity={0.5} label={{ value: 'Priorité', fontSize: 10, fill: CHART_COLORS.tertiary }} />
                <ReferenceArea x1={0.30} x2={1} y1={medianSize} y2={scatterData.reduce((max, m) => Math.max(max, m.market_size), 0) * 1.1}
                  fill="#E8F8F4" fillOpacity={0.5} label={{ value: 'Leader', fontSize: 10, fill: CHART_COLORS.primary }} />
                <Tooltip content={<MarketTooltip />} />
                <Scatter data={scatterData} onClick={(d: any) => navigate(`/sdi/markets/${d.id}`)} cursor="pointer">
                  {scatterData.map((m, i) => (
                    <Cell key={i} fill={m.confidence_score >= 70 ? CHART_COLORS.primary : m.confidence_score >= 40 ? CHART_COLORS.secondary : CHART_COLORS.tertiary}
                      fillOpacity={0.7} stroke="#fff" strokeWidth={1} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Activity Feed */}
        <div className="bg-white rounded-card border border-ink-border shadow-card">
          <div className="px-5 py-4 border-b border-ink-border/30">
            <h3 className="font-serif text-sm font-semibold text-ink">Activité récente</h3>
          </div>
          <div className="divide-y divide-ink-border/30 max-h-[360px] overflow-y-auto">
            {!(recentJobs as JobRunItem[])?.length ? (
              <p className="p-5 text-[12px] font-light text-ink-faint text-center">Aucune activité</p>
            ) : (recentJobs as JobRunItem[]).map((j) => (
              <div key={j.id} className="px-5 py-3 flex items-start gap-3 hover:bg-gold-light/20 transition-colors">
                <div className="mt-0.5">{jobIcon(j.status)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium text-ink truncate">{j.job_name}</p>
                  <p className="text-[10px] font-light text-ink-faint">{j.rows_processed} lignes · {timeAgo(j.started_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Opportunities — onyx header */}
      {topOpps.length > 0 && (
        <div className="bg-white rounded-card border border-ink-border shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-ink-border/30">
            <h3 className="font-serif text-sm font-semibold text-ink">Top opportunités</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-onyx text-paper">
                <th className="text-left px-4 py-3 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">#</th>
                <th className="text-left px-4 py-3 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Marché</th>
                <th className="text-right px-4 py-3 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Opportunité</th>
                <th className="text-right px-4 py-3 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Capture</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {topOpps.map((m, i) => (
                <tr key={m.id}
                  className={cn('hover:bg-gold-light/30 transition-colors cursor-pointer', i % 2 === 1 && 'bg-paper')}
                  onClick={() => navigate(`/sdi/markets/${m.id}`)}>
                  <td className="px-4 py-3 text-ink-faint font-mono text-[11px] border-t border-ink-border/20">{i + 1}</td>
                  <td className="px-4 py-3 text-[13px] font-medium text-ink border-t border-ink-border/20">{m.name}</td>
                  <td className="px-4 py-3 text-right text-kpi text-sm text-semantic-lost border-t border-ink-border/20">{fmtEur.format(m.lost_value)}</td>
                  <td className="px-4 py-3 text-right text-[13px] text-ink border-t border-ink-border/20">{fmtPct.format(m.capture_rate)}</td>
                  <td className="px-4 py-3 border-t border-ink-border/20" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => navigate(`/sdi/markets/${m.id}/budget`)}
                      className="text-[11px] font-medium text-brick flex items-center gap-0.5 hover:text-brick-hover transition-colors">
                      Simuler <ChevronRight size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
